import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface DuplicateGroup {
  /** Normalized key used for grouping */
  key: string;
  /** The customer IDs in this group */
  ids: string[];
  /** The full customer records */
  customers: any[];
  /** Why these are considered duplicates */
  reason: string;
}

/**
 * Normalize a string for fuzzy comparison:
 * lowercase, strip punctuation/extra spaces, remove common suffixes
 */
function normalize(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "") // strip punctuation
    .replace(/\s+/g, " ")         // collapse whitespace
    .replace(/\b(llc|inc|corp|co|ltd|company|group|enterprises?|services?|construction|contracting)\b/g, "")
    .trim();
}

/** Strip non-digit characters from phone for comparison */
function normalizePhone(p: string | null | undefined): string {
  if (!p) return "";
  return p.replace(/\D/g, "").slice(-10); // last 10 digits
}

function normalizeEmail(e: string | null | undefined): string {
  if (!e) return "";
  return e.toLowerCase().trim();
}

/**
 * Detect duplicate customer groups.
 * Two customers are potential duplicates if ANY of:
 * 1. Normalized name is identical (strongest signal)
 * 2. Same non-empty email address
 * 3. Same non-empty phone (last 10 digits)
 */
function detectDuplicates(customers: any[]): DuplicateGroup[] {
  const nameMap = new Map<string, any[]>();
  const emailMap = new Map<string, any[]>();
  const phoneMap = new Map<string, any[]>();

  for (const c of customers) {
    const nName = normalize(c.name);
    const nEmail = normalizeEmail(c.email);
    const nPhone = normalizePhone(c.phone);

    if (nName) {
      if (!nameMap.has(nName)) nameMap.set(nName, []);
      nameMap.get(nName)!.push(c);
    }
    if (nEmail) {
      if (!emailMap.has(nEmail)) emailMap.set(nEmail, []);
      emailMap.get(nEmail)!.push(c);
    }
    if (nPhone && nPhone.length >= 7) {
      if (!phoneMap.has(nPhone)) phoneMap.set(nPhone, []);
      phoneMap.get(nPhone)!.push(c);
    }
  }

  // Merge groups: use Union-Find to connect customers that share any attribute
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    if (!parent.has(id)) parent.set(id, id);
    if (parent.get(id) !== id) parent.set(id, find(parent.get(id)!));
    return parent.get(id)!;
  };
  const union = (a: string, b: string) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  // Track reasons per customer pair
  const reasonMap = new Map<string, Set<string>>();
  const addReason = (ids: string[], reason: string) => {
    for (const id of ids) {
      if (!reasonMap.has(id)) reasonMap.set(id, new Set());
      reasonMap.get(id)!.add(reason);
    }
  };

  for (const [, group] of nameMap) {
    if (group.length < 2) continue;
    const ids = group.map((c: any) => c.id);
    addReason(ids, "Same name");
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }
  for (const [email, group] of emailMap) {
    if (group.length < 2) continue;
    const ids = group.map((c: any) => c.id);
    addReason(ids, `Same email (${email})`);
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }
  for (const [, group] of phoneMap) {
    if (group.length < 2) continue;
    const ids = group.map((c: any) => c.id);
    addReason(ids, "Same phone");
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }

  // Collect groups
  const groups = new Map<string, any[]>();
  for (const c of customers) {
    const root = find(c.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(c);
  }

  const result: DuplicateGroup[] = [];
  for (const [, members] of groups) {
    if (members.length < 2) continue;
    // Combine all reasons
    const reasons = new Set<string>();
    for (const m of members) {
      for (const r of reasonMap.get(m.id) ?? []) reasons.add(r);
    }
    result.push({
      key: members[0].id,
      ids: members.map((c: any) => c.id),
      customers: members,
      reason: Array.from(reasons).join(", "),
    });
  }

  return result.sort((a, b) => b.customers.length - a.customers.length);
}

/**
 * Hook: fetch all active customers and detect duplicates.
 * Returns duplicate groups and a Set of all customer IDs that are duplicates.
 */
export function useCustomerDuplicates() {
  const { user } = useAuth();

  const { data: allCustomers, isLoading } = useQuery({
    queryKey: ["customers_all_for_duplicates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("is_deleted", false)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
    staleTime: 60_000, // cache for 1 minute
  });

  const { groups, duplicateIds } = useMemo(() => {
    if (!allCustomers?.length) return { groups: [] as DuplicateGroup[], duplicateIds: new Set<string>() };
    const g = detectDuplicates(allCustomers);
    const ids = new Set<string>();
    for (const group of g) {
      for (const id of group.ids) ids.add(id);
    }
    return { groups: g, duplicateIds: ids };
  }, [allCustomers]);

  return { groups, duplicateIds, isLoading, allCustomers: allCustomers ?? [] };
}

/**
 * Find potential matches for a customer being created/edited.
 * Checks name, email, and phone against existing customers.
 */
export function findPotentialMatches(
  allCustomers: any[],
  name: string,
  email: string,
  phone: string,
  excludeId?: string,
): { customer: any; reasons: string[] }[] {
  const nName = normalize(name);
  const nEmail = normalizeEmail(email);
  const nPhone = normalizePhone(phone);

  if (!nName && !nEmail && !nPhone) return [];

  const matches: { customer: any; reasons: string[] }[] = [];

  for (const c of allCustomers) {
    if (excludeId && c.id === excludeId) continue;

    const reasons: string[] = [];
    if (nName && normalize(c.name) === nName) reasons.push("Same name");
    if (nEmail && normalizeEmail(c.email) === nEmail) reasons.push("Same email");
    if (nPhone && nPhone.length >= 7 && normalizePhone(c.phone) === nPhone) reasons.push("Same phone");

    if (reasons.length > 0) matches.push({ customer: c, reasons });
  }

  return matches;
}
